import {
    InputHTMLAttributes,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';

import { QUIZ_EVENT_TYPES } from 'src/types/Quiz';
import { QuizContext } from 'src/views/Quiz/Quiz.context';

import { addTraits } from 'src/analytics/Analytics';
import { Checkbox, Input } from '@get-mosh/msh-shared';
import { env, isMosh } from 'src/env.mjs';
import Image from 'next/image';
import {
    parsePhoneNumberFromString,
    formatIncompletePhoneNumber,
} from 'libphonenumber-js';
import { isCountry } from 'src/constants/Countries';
import Routes from 'src/constants/Routes';
import Link from 'next/link';
import { useUpdateContactPreferences } from 'src/hooks/User/ContactPreferences/useUpdateContactPreferences';
import { smsPreferenceRequestPayload } from 'src/hooks/User/ContactPreferences/types';

export interface QuizPhoneNumberFieldProps
    extends InputHTMLAttributes<HTMLInputElement> {
    title?: string;
    image?: {
        src: string;
        height: number;
        width: number;
    };
    modalHeading?: string;
    modalDescription?: string;
}

const QuizPhoneNumberField = () => {
    const {
        currentQuestion,
        quizToken,
        handleSubmitAnswer,
        state,
        handleNavigateToQuestion,
    } = useContext(QuizContext);
    const { questionId, questionText, answerList, userAnswers, medication } =
        currentQuestion || {};
    const { frontendStamp: answerId } = answerList[0] || {};
    const [continueDisabled, setContinueDisabled] = state.continueDisabled;
    const [phoneNumber, setPhoneNumber] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');
    const [isError, setIsError] = useState<boolean>(false);
    const [isTyping, setIsTyping] = useState<boolean>(false);
    const [touched, setTouched] = useState<boolean>(false);
    const [isSmsOptedIn, setIsSmsOptedIn] = useState<boolean>(true);

    const validateInput = (value: string) => {
        const errorText = isCountry('AU')
            ? 'Enter a 10-digit number starting with 04'
            : 'Enter an 8–10 digit NZ mobile number starting with 02';
        const phoneNumber = parsePhoneNumberFromString(
            value,
            env.NEXT_PUBLIC_COUNTRY_CODE,
        );
        const isValid = phoneNumber?.isValid() ?? false;

        setIsError(!isValid);
        setErrorMessage(isValid ? '' : errorText);

        return isValid;
    };

    const handleBlur = () => {
        setIsTyping(false);
        setTouched(true);
        validateInput(phoneNumber);
        const formatted = formatIncompletePhoneNumber(
            phoneNumber,
            env.NEXT_PUBLIC_COUNTRY_CODE,
        );
        setPhoneNumber(formatted);
    };

    const handleFocus = () => {
        setIsTyping(true);
    };

    const { mutateAsync: updateContactPreferences } =
        useUpdateContactPreferences();

    const strippedPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');

    useEffect(() => {
        const isValid = validateInput(phoneNumber);
        setContinueDisabled(!isValid);
    }, [phoneNumber, setContinueDisabled]);

    // Set existing answer
    useEffect(() => {
        let newAnswerText: string | null = null;
        if (userAnswers.length > 0) {
            newAnswerText = userAnswers[0].answerText;
        }
        if (newAnswerText) {
            setPhoneNumber(newAnswerText);
        }
    }, [questionId, userAnswers]);

    // Handle submitting
    const handleSubmit = useCallback(() => {
        if (!continueDisabled && questionId) {
            setIsError(false);
            if (isCountry('NZ')) {
                updateContactPreferences({
                    quizToken: quizToken || '',
                    subscribe: isSmsOptedIn ? smsPreferenceRequestPayload : [],
                    unsubscribe: isSmsOptedIn
                        ? []
                        : smsPreferenceRequestPayload,
                });
            }
            handleSubmitAnswer(
                {
                    questionId: questionId,
                    questionText: questionText,
                    answerIds: answerId ? [answerId] : undefined,
                    answerTexts: [strippedPhoneNumber],
                    medication: medication ? medication : '',
                },
                (response) => {
                    addTraits({
                        phone: strippedPhoneNumber,
                    });
                    handleNavigateToQuestion(response.currentQuestionId);
                },
            );
        } else {
            setIsError(true);
        }
    }, [
        continueDisabled,
        questionId,
        updateContactPreferences,
        quizToken,
        isSmsOptedIn,
        handleSubmitAnswer,
        questionText,
        answerId,
        strippedPhoneNumber,
        medication,
        handleNavigateToQuestion,
    ]);

    // Continue click listener
    useEffect(() => {
        window.addEventListener(QUIZ_EVENT_TYPES.continueClick, handleSubmit);

        return () =>
            window.removeEventListener(
                QUIZ_EVENT_TYPES.continueClick,
                handleSubmit,
            );
    }, [handleSubmit]);

    // Enter key press listener
    useEffect(() => {
        const handleKeyPress = (e: KeyboardEvent) => {
            if (e.key === 'Enter') handleSubmit();
        };
        window.addEventListener('keydown', handleKeyPress, false);

        return () =>
            window.removeEventListener('keydown', handleKeyPress, false);
    }, [handleSubmit]);

    return (
        <div className="mx-auto flex max-w-[400px] flex-col gap-4">
            <Image
                src={
                    isMosh
                        ? '/user/images/mosh/phone.png'
                        : '/user/images/moshy/phone.png'
                }
                height={isMosh ? 48 : 96}
                width={isMosh ? 64 : 96}
                alt="Phone icon"
                className={isMosh ? 'mx-4' : ''}
            />
            <div className="flex flex-col gap-2">
                <h4>{questionText}</h4>
                <p className="text-grey-400">
                    This is so we can notify you when a practitioner has
                    recommended your treatment plan
                </p>
            </div>
            <Input
                data-testid={`phone-number-input-${env.NEXT_PUBLIC_COUNTRY_CODE}`}
                type="tel"
                placeholder={
                    isCountry('AU') ? 'eg. 0400 000 000' : 'eg. 020 000 0000'
                }
                value={phoneNumber}
                onChange={(e) => {
                    setPhoneNumber(e.target.value);
                }}
                onBlur={handleBlur}
                onFocus={handleFocus}
                error={touched && !isTyping && isError ? errorMessage : ''}
                maxLength={isCountry('AU') ? 12 : 13}
            />
            {isCountry('NZ') ? (
                <>
                    <Checkbox
                        defaultChecked
                        onChange={(e) => {
                            setIsSmsOptedIn(e.target.checked);
                        }}
                        label="I agree to receive occasional text messages from Moshy about marketing news, updates or offers. You can opt out at any time."
                    />
                    <span className="text-grey-400 p3 [&_a]:underline">
                        Read our{' '}
                        <Link href={Routes.TERMS_OF_USE} target="_blank">
                            Terms of user
                        </Link>{' '}
                        and{' '}
                        <Link href={Routes.PRIVACY_POLICY} target="_blank">
                            Privacy Policy
                        </Link>
                    </span>
                </>
            ) : null}
        </div>
    );
};

export default QuizPhoneNumberField;
